import React, { useState } from 'react';
import { Users, Search, Plus, MessageSquare, Calendar, TrendingUp, Lock, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockGroups } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Groups = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [joinedGroups, setJoinedGroups] = useState(['1', '2']);

  const handleJoinGroup = (groupId) => {
    if (joinedGroups.includes(groupId)) {
      setJoinedGroups(joinedGroups.filter(id => id !== groupId));
      toast({
        title: "Left group",
        description: "You have successfully left the group.",
      });
    } else {
      setJoinedGroups([...joinedGroups, groupId]);
      toast({
        title: "Joined group!",
        description: "You have successfully joined the group.",
      });
    }
  };

  const discoveredGroups = [
    ...mockGroups,
    {
      id: "4",
      name: "VR Training Pioneers",
      members: 15600,
      image: "https://images.unsplash.com/photo-1592478411213-6153e4ebc696?w=100&h=100&fit=crop",
      description: "Exploring virtual and augmented reality applications in professional training"
    },
    {
      id: "5",
      name: "Training ROI Specialists",
      members: 8900,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=100&h=100&fit=crop",
      description: "Measuring and maximizing return on investment in training programs"
    }
  ];

  const filteredGroups = discoveredGroups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <Users className="w-6 h-6 mr-3 text-blue-600" />
            Training Groups
          </CardTitle>
          <p className="text-sm text-gray-600">
            Connect with training professionals and join specialized communities
          </p>
        </CardHeader>
      </Card>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search groups by name or topic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button className="flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Create group
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="my-groups" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="my-groups">
            My Groups ({joinedGroups.length})
          </TabsTrigger>
          <TabsTrigger value="discover">
            Discover Groups
          </TabsTrigger>
          <TabsTrigger value="requests">
            Requests (0)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-groups" className="space-y-4">
          {joinedGroups.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No groups joined yet</h3>
                <p className="text-gray-600 mb-4">Join training communities to connect with like-minded professionals and share knowledge.</p>
                <Button>Discover Groups</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {filteredGroups.filter(group => joinedGroups.includes(group.id)).map((group) => (
                <Card key={group.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-6">
                      <Avatar className="w-20 h-20">
                        <AvatarImage src={group.image} alt={group.name} />
                        <AvatarFallback>{group.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-xl hover:text-blue-600 cursor-pointer">
                              {group.name}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                {group.members.toLocaleString()} members
                              </div>
                              <div className="flex items-center">
                                <Globe className="w-4 h-4 mr-1" />
                                Public
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleJoinGroup(group.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Leave
                          </Button>
                        </div>
                        <p className="text-gray-700 mb-4">{group.description}</p>
                        <div className="flex items-center space-x-4">
                          <Button size="sm" variant="outline" className="flex items-center">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            View discussions
                          </Button>
                          <Button size="sm" variant="outline" className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2" />
                            Events
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discover" className="space-y-4">
          <div className="grid gap-6">
            {filteredGroups.map((group) => (
              <Card key={group.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-6">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={group.image} alt={group.name} />
                      <AvatarFallback>{group.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-xl hover:text-blue-600 cursor-pointer">
                            {group.name}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {group.members.toLocaleString()} members
                            </div>
                            <div className="flex items-center">
                              <Globe className="w-4 h-4 mr-1" />
                              Public
                            </div>
                            <div className="flex items-center">
                              <TrendingUp className="w-4 h-4 mr-1" />
                              Active
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleJoinGroup(group.id)}
                          variant={joinedGroups.includes(group.id) ? "outline" : "default"}
                          size="sm"
                        >
                          {joinedGroups.includes(group.id) ? "Joined" : "Join"}
                        </Button>
                      </div>
                      <p className="text-gray-700 mb-4">{group.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-1" />
                          {Math.floor(Math.random() * 50) + 10} posts this week
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Next event in 3 days
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No pending requests</h3>
              <p className="text-gray-600 mb-4">
                When you request to join private groups, they will appear here for approval.
              </p>
              <Button variant="outline">Discover Private Groups</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Groups;